import { useState } from "react";
import axios from "axios";
import { Layers, Plus, Pencil, Trash2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { PageToggles } from "@/components/PageToggles";
import { API } from "@/context/AuthContext";
import { toast } from "sonner";

export const PresetSelect = ({ presets, value, onChange, testId, placeholder = "Apply preset…" }) => (
  <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId}
    className="h-9 rounded-md border border-slate-300 bg-white px-2.5 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-sky-500">
    <option value="">{placeholder}</option>
    {presets.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.pages.length})</option>)}
  </select>
);

export function AccessPresets({ presets, pages, onChanged }) {
  const [editing, setEditing] = useState(null); // preset | "new"
  const [form, setForm] = useState({ name: "", pages: [] });

  const open = (p) => {
    setEditing(p);
    setForm(p === "new" ? { name: "", pages: [] } : { name: p.name, pages: p.pages });
  };
  const save = async () => {
    try {
      if (editing === "new") await axios.post(`${API}/admin/presets`, form);
      else await axios.patch(`${API}/admin/presets/${editing.id}`, form);
      toast.success(editing === "new" ? "Preset created" : "Preset updated");
      setEditing(null);
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save preset"); }
  };
  const remove = async (p) => {
    if (!window.confirm(`Delete preset "${p.name}"? Users keep their current access.`)) return;
    try {
      await axios.delete(`${API}/admin/presets/${p.id}`);
      toast.success("Preset deleted");
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to delete preset"); }
  };
  const label = (k) => pages.find((p) => p.key === k)?.label || k;

  return (
    <Card className="border-slate-200 shadow-sm" data-testid="access-presets-card">
      <CardHeader className="pb-3 flex-row items-center justify-between space-y-0">
        <div className="flex items-center gap-2.5">
          <Layers className="h-5 w-5 text-slate-500" />
          <div>
            <CardTitle className="text-lg font-semibold text-slate-800">Access Presets</CardTitle>
            <p className="text-xs text-slate-500">Report bundles you can apply to a user in one click</p>
          </div>
        </div>
        <Button size="sm" variant="outline" onClick={() => open("new")} data-testid="preset-new-button"><Plus className="h-3.5 w-3.5 mr-1" />New preset</Button>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {presets.map((p) => (
            <div key={p.id} className="rounded-md border border-slate-200 p-3 flex items-start justify-between gap-2" data-testid={`preset-card-${p.id}`}>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-slate-800" data-testid={`preset-name-${p.id}`}>{p.name}</p>
                <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{p.pages.length ? p.pages.map(label).join(" · ") : "No reports"}</p>
              </div>
              <div className="flex gap-1 shrink-0">
                <Button size="sm" variant="outline" className="h-7 w-7 p-0" onClick={() => open(p)} data-testid={`preset-edit-${p.id}`}><Pencil className="h-3 w-3" /></Button>
                <Button size="sm" variant="outline" className="h-7 w-7 p-0 border-red-200 text-red-600 hover:bg-red-50" onClick={() => remove(p)} data-testid={`preset-delete-${p.id}`}><Trash2 className="h-3 w-3" /></Button>
              </div>
            </div>
          ))}
          {presets.length === 0 && <p className="text-sm text-slate-400 py-2">No presets yet — create one to grant report bundles quickly.</p>}
        </div>
      </CardContent>

      <Dialog open={editing !== null} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg" data-testid="preset-dialog">
          <DialogHeader>
            <DialogTitle>{editing === "new" ? "New preset" : `Edit preset — ${editing?.name || ""}`}</DialogTitle>
            <DialogDescription>Name the bundle and tick the reports it grants.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Preset name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. AP team" data-testid="preset-form-name" />
            </div>
            <div className="space-y-1.5">
              <Label>Reports in this preset ({form.pages.length})</Label>
              <PageToggles pages={pages} value={form.pages} onChange={(v) => setForm({ ...form, pages: v })} idPrefix="preset-toggle" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} data-testid="preset-form-cancel">Cancel</Button>
            <Button onClick={save} disabled={!form.name.trim()} className="bg-sky-700 hover:bg-sky-800 text-white" data-testid="preset-form-save">{editing === "new" ? "Create preset" : "Save preset"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
